import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, Target, BookOpen, Menu, X, ExternalLink } from "lucide-react";
import { Switch } from "@/components/ui/switch";

export default function DashboardLayout({ children, userRole, setUserRole }) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: "Home", href: "/", icon: Home },
    { name: "Goals & Impact", href: "/goals-impact", icon: Target },
    { name: "Education", href: "/education", icon: BookOpen },
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 navy-gradient transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between px-6 py-6 border-b border-white/10">
            <div className="flex items-center space-x-3">
              <img
                src="https://customer-assets.emergentagent.com/job_a61284df-68cb-4f82-b4d6-5cabd88681a3/artifacts/frjmmhxb_IMG_2281.jpeg"
                alt="Anchor Lane"
                className="h-12 w-auto"
                data-testid="company-logo"
              />
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-white hover:text-gray-200"
              data-testid="close-sidebar-btn"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2">
            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}-link`}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg font-medium transition-colors ${
                    active
                      ? "bg-white/20 text-white"
                      : "text-white/80 hover:bg-white/10 hover:text-white"
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.name}</span>
                </Link>
              );
            })}

            {/* Website Link */}
            <a
              href="https://anchorllane.wixsite.com/anchor-lane/about"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-3 px-4 py-3 rounded-lg font-medium text-white/80 hover:bg-white/10 hover:text-white transition-colors"
              data-testid="main-website-link"
            >
              <ExternalLink className="h-5 w-5" />
              <span>Our Website</span>
            </a>
          </nav>

          {/* Role Switcher */}
          <div className="px-6 py-6 border-t border-white/10">
            <div className="bg-white/10 backdrop-blur-md rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-white text-sm font-medium">View Mode</span>
                <Switch
                  checked={userRole === "team"}
                  onCheckedChange={(checked) =>
                    setUserRole(checked ? "team" : "client")
                  }
                  data-testid="role-switcher"
                  className="data-[state=checked]:bg-[#C5A059]"
                />
              </div>
              <div className="text-white/60 text-xs">
                {userRole === "client" ? "Client View" : "Team View"}
              </div>
            </div>
          </div>

          {/* Footer Text */}
          <div className="px-6 py-4 border-t border-white/10">
            <p className="text-white/60 text-xs text-center">
              Empowering Financial Independence
            </p>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="bg-white border-b border-slate-200 px-6 py-4 lg:px-8">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-600 hover:text-gray-900"
              data-testid="open-sidebar-btn"
            >
              <Menu className="h-6 w-6" />
            </button>
            <div className="flex items-center space-x-4 ml-auto">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">Welcome to Anchor Lane</p>
                <p className="text-xs text-gray-500 capitalize">{userRole} Portal</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-slate-50">
          {children}
        </main>
      </div>
    </div>
  );
}
